import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { DirectionsButton } from "../../ui/Directions";
import { useSpots } from "../../data/weatherQueries";
import { EmptyState } from "../../ui/StatusBadge";
import { WeatherIcon } from "../../ui/WeatherIcon";
import { useNow } from "../../ui/time";
import { SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { fcTemp } from "../weather/WeatherWidget";
import { WidgetHeader } from "../WidgetHeader";
import "./outdoors.css";

interface SpotsConfig {
  periods: number;
}

/** NWS forecasts for the family's favorite trailheads, ski areas and campgrounds. */
function Spots({ config, size, fullscreen, openFullscreen }: WidgetProps<SpotsConfig>) {
  const { settings, isAdmin } = useHousehold();
  useNow("minute");
  const q = useSpots();
  const state = dataStateOf(q, 3 * 60 * 60_000);
  if (settings.outdoorSpots.length === 0)
    return (
      <EmptyState
        icon="mountain"
        title="Add your favorite places"
        detail="Pick trailheads, ski areas or campgrounds on the map to see their forecasts here."
        action={
          isAdmin ? (
            <a className="btn btn-sm btn-primary" href="/admin#outdoors">
              Pick places
            </a>
          ) : undefined
        }
      />
    );
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="mountain" title="Mountain forecasts unavailable" detail={q.error?.message} />;
  const n = fullscreen ? 6 : size.width < 520 ? Math.min(2, config.periods) : config.periods;

  return (
    <div className="outdoor spots">
      <WidgetHeader title="Our places" meta="NWS point forecasts" onTitle={fullscreen ? undefined : openFullscreen} />
      <ul className="spot-list">
        {data.spots.map(({ spot, periods, error }) => (
          <li key={spot.id} className="spot">
            <span className="spot-name">
              <strong>{spot.name}</strong>
              <DirectionsButton target={spot} className="spot-dir icon-btn" iconOnly />
            </span>
            {error || periods.length === 0 ? (
              <span className="muted">{error ?? "No forecast"}</span>
            ) : (
              <span className="spot-periods">
                {periods.slice(0, n).map((p) => (
                  <span key={p.startTime} className={`spot-period ${p.isDaytime ? "" : "is-night"}`} title={p.detailedForecast}>
                    <small>{p.name}</small>
                    <WeatherIcon kind={p.icon} isDaytime={p.isDaytime} size={30} />
                    <b>{fcTemp(p, settings.temperatureUnit)}</b>
                    <span className="spot-short">{p.shortForecast}</span>
                  </span>
                ))}
              </span>
            )}
          </li>
        ))}
      </ul>
      {size.variant !== "compact" && <SkyFoot source="National Weather Service" state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

export const spotsWidget: WidgetDefinition<SpotsConfig> = {
  type: "outdoor-spots",
  version: 1,
  name: "Our places",
  description: "Forecasts for your favorite trailheads, ski areas and campgrounds, picked on the map in Admin.",
  icon: "mountain",
  category: "weather",
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 3, h: 3 },
  stackHeight: 400,
  defaults: { periods: 4 },
  settings: [
    {
      key: "periods",
      label: "Forecast shown",
      type: "select",
      options: [
        { value: 2, label: "Today and tonight" },
        { value: 4, label: "Two days" },
        { value: 6, label: "Three days" },
      ],
    },
  ],
  provider: "nws",
  supportsFullscreen: true,
  Component: Spots,
};
